import * as Configuration from './configuration'
import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ThemeUi from 'theme-ui'

import PropTypes from 'prop-types'
import {defaultTheme} from './theme'

const OVERRIDES = {
  casitabi: {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      background: 'transparent',
      muted: '#3765af',
      navBackground: '#2653a1',
      paymentMethodText: '#343a40',
      primary: '#00ca1b',
      formLabel: '#ffffff',
      text: '#ffffff',
      link: '#ffffff',
    },
    buttons: {
      ...defaultTheme.buttons,
      primary: {
        ...defaultTheme.buttons.primary,
        borderRadius: '9999px',
      },
    },
    forms: {
      ...defaultTheme.forms,
      input: {
        ...defaultTheme.forms.input,
        color: 'paymentMethodText',
      },
      select: {
        ...defaultTheme.forms.select,
        color: 'paymentMethodText',
      },
    },
  },
  nolimitcasino: {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      background: 'transparent',
      muted: '#005236',
      navBackground: '#0B2A32',
      paymentMethodText: '#F8F7F1',
      primary: '#009966',
      formLabel: '#C8CCC9',
      text: '#F8F7F1',
    },
    buttons: {
      ...defaultTheme.buttons,
      primary: {
        ...defaultTheme.buttons.primary,
        'px': 2,
        'borderRadius': '4px',
        'boxShadow':
          '0px 1px 8px rgba(0, 0, 0, 0.25), inset 0px 1px 0px rgba(255, 255, 255, 0.25)',
        'textShadow': '0px 1px 0px rgba(0, 0, 0, 0.25)',
        'height': '44px',
        '&:hover': {
          background: 'hsl(160, 100%, 34%)',
        },
      },
    },
    forms: {
      ...defaultTheme.forms,
      input: {
        ...defaultTheme.forms.input,
        'color': 'paymentMethodText',
        'backgroundColor': '#072027',
        '&:focus': {
          borderColor: 'paymentMethodText',
          outline: 'none',
        },
      },
      select: {
        ...defaultTheme.forms.select,
        color: 'paymentMethodText',
        backgroundColor: '#072027',
      },
    },
  },
  legendz: {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      background: 'transparent',
      muted: '#FAFAFA', // neutral-25 (white divider)
      navBackground: 'transparent',
      paymentMethodText: '#F0F1F1', // neutral-100
      primary: '#9A44F1', // primary-500
      secondary: '#17B26A', // secondary-500
      formLabel: '#E7D0FE', // primary-200
      text: '#FAFAFA', // neutral-25
      link: '#B978FA', // primary-400
      accent: '#F38744', // tertiary-400
      success: '#17B26A', // success-500
      warning: '#F79009', // warning-500
      error: '#F04438', // alert-500
      paymentMethodBackground: '#242B3B', // neutral-800
      paymentMethodImageFilter: 'brightness(1.2)',
    },
    buttons: {
      ...defaultTheme.buttons,
      primary: {
        ...defaultTheme.buttons.primary,
        'px': 3,
        'borderRadius': '8px',
        'background':
          'linear-gradient(180deg, #B978FA 0%, #9A44F1 50%, #7C24D5 100%)',
        'boxShadow':
          '0px 0px 4px 0px #7C24D5, 0px 0px 2px 0px #FFFFFF, 0px 0px 12px 0px #B978FA',
        'textShadow': '0px 1px 0px rgba(0, 0, 0, 0.25)',
        'height': '44px',
        'fontWeight': 600,
        '&:hover': {
          background:
            'linear-gradient(180deg, #D3AAFD 0%, #B978FA 50%, #9A44F1 100%)',
        },
        '&:active': {
          background: '#651AB1',
        },
      },
      secondary: {
        ...defaultTheme.buttons?.secondary,
        'px': 3,
        'borderRadius': '8px',
        'backgroundColor': 'transparent',
        'border': '1px solid #9A44F1',
        'color': '#B978FA',
        'height': '44px',
        '&:hover': {
          backgroundColor: 'rgba(154, 68, 241, 0.1)',
          borderColor: '#B978FA',
        },
      },
      close: {
        'cursor': 'pointer',
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'p': '8px',
        'bg': '#1B202B',
        'background': '#1B202B',
        'borderRadius': '8px',
        'color': '#F0F1F1',
        'border': 'none',
        'boxShadow': 'none',
        'textShadow': 'none',
        'height': 'auto',
        'width': 'auto',
        'minWidth': 'auto',
        'transition': 'all 150ms ease-in-out',
        '&:hover': {
          bg: '#242B3B',
          background: '#242B3B',
          color: '#FAFAFA',
        },
      },
    },
    forms: {
      ...defaultTheme.forms,
      input: {
        ...defaultTheme.forms.input,
        'color': 'paymentMethodText',
        'backgroundColor': '#1D2230', // neutral-900
        'borderColor': '#404966', // neutral-500
        'borderRadius': '8px',
        '&:focus': {
          borderColor: '#9A44F1', // primary-500
          outline: 'none',
          boxShadow: '0px 0px 4px 0px rgba(154, 68, 241, 0.5)',
        },
        '&::placeholder': {
          color: '#5E6C96', // neutral-400
        },
      },
      select: {
        ...defaultTheme.forms.select,
        'color': 'paymentMethodText',
        'backgroundColor': '#1D2230', // neutral-900
        'borderColor': '#404966', // neutral-500
        'borderRadius': '8px',
        '&:focus': {
          borderColor: '#9A44F1', // primary-500
          outline: 'none',
        },
      },
      label: {
        color: '#E7D0FE', // primary-200
        fontWeight: 500,
      },
    },
    cards: {
      primary: {
        background:
          'radial-gradient(89.53% 92.72% at 50% 117.43%, #471877 0%, #2E104C 100%)',
        borderRadius: '14px',
        border: '1px solid #7C24D5',
        boxShadow: '0px 0px 4px 0px #7C24D5, 0px 0px 2px 0px #FFFFFF',
      },
    },
  },
}

export function BrandThemeProvider(props) {
  const brand = ReactRedux.useSelector(Configuration.getBrandingId)
  const theme = OVERRIDES[brand] || {
    ...defaultTheme,
    buttons: {
      ...defaultTheme.buttons,
    },
  }

  return (
    <ThemeUi.ThemeProvider theme={theme}>
      {props.children}
    </ThemeUi.ThemeProvider>
  )
}

BrandThemeProvider.propTypes = {
  children: PropTypes.node,
}
